/**
 * Used to select files to upload and show status while uploading.
 */

// Libraries
import { useState } from "react";
import { useForm, useFieldArray, type SubmitHandler } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Spinner } from "react-bootstrap";
import { useRouteContext } from "@tanstack/react-router";
import { TbFileText } from "react-icons/tb";

// Scripts
import { getCandidateSubmissionsQueryKey } from "@/queries.ts";
import { fetchAuthenticated } from "@/queries/shared";
import { compareFileNames, readFileContents } from "@/utils/files";
import { formatNumber } from "@/utils/numbers";

// Components
import Card from "@/components/shared/Card";
import LoaderButton from "@/components/shared/LoaderButton";
import TooltipTrigger from "@/components/shared/TooltipTrigger";
import BrowseFilesButton from "@/components/file-submission/UploadFilesCard/BrowseFilesButton";
import FullPageDropZone from "@/components/file-submission/UploadFilesCard/FullPageDropZone";
import {
  type RecordType,
  fileUploadRecordTypes,
  type FileEntry,
} from "@/models.ts";
import { recordTypeLabels } from "@/constants/lookups.ts";
import Icon from "@/components/shared/Icon";
import Alert from "@/components/shared/Alert";

interface FileResponse extends FileEntry {
  status?: "Uploading" | "Uploaded" | "Failed";
  error?: string;
}

type UploadData = {
  files: FileEntry[];
};

type ValidatedFiles = {
  invalidTypeFiles: File[];
  duplicateNameFiles: File[];
  validFiles: File[];
};

const LINE_HEIGHT = "2.125rem";

const fileTypes = ["text/csv"];
const fileExtensions = [".csv"];
const fileTypesLabel = ".csv";

/**
 * Returns true if the file is an accepted type.
 * Checks the file extension OR the browser-reported MIME type, accepting the file
 * if either matches. Extension is the reliable signal: browsers (notably Firefox)
 * often report an empty or unexpected MIME type for .csv files depending on the
 * machine's file-type registry, so we cannot depend on MIME alone.
 */
function isAcceptedType(
  file: File,
  acceptedTypes?: string[],
  acceptedExtensions?: string[]
): boolean {
  const extensionMatches =
    acceptedExtensions?.some((ext) =>
      file.name.toLowerCase().endsWith(ext.toLowerCase())
    ) ?? false;
  const mimeMatches = acceptedTypes?.includes(file.type) ?? false;
  return extensionMatches || mimeMatches;
}

function validateFiles(
  newFiles: File[],
  existingFiles: File[],
  acceptedTypes?: string[],
  acceptedExtensions?: string[]
): ValidatedFiles {
  const existingNames = new Set(existingFiles.map((f) => f.name));

  // Only enforce a type restriction if one was actually provided.
  const hasTypeRestriction =
    (acceptedTypes != null && acceptedTypes.length > 0) ||
    (acceptedExtensions != null && acceptedExtensions.length > 0);

  return newFiles.reduce(
    (acc, file) => {
      if (
        hasTypeRestriction &&
        !isAcceptedType(file, acceptedTypes, acceptedExtensions)
      ) {
        acc.invalidTypeFiles.push(file);
      } else if (existingNames.has(file.name)) {
        acc.duplicateNameFiles.push(file);
      } else {
        acc.validFiles.push(file);
      }
      return acc;
    },
    {
      invalidTypeFiles: [],
      duplicateNameFiles: [],
      validFiles: [],
    } as ValidatedFiles
  );
}

const autoSelectRecordTypes = async (
  entries: FileEntry[]
): Promise<FileEntry[]> => {
  return await Promise.all(
    entries.map(async (entry) => {
      entry.recordType = await getDefaultRecordType(entry.file);
      return entry;
    })
  );
};

const getDefaultRecordType = async (
  recordFile: File
): Promise<RecordType | ""> => {
  const objPattern = new RegExp(
    "(\\,|\\r?\\n|\\r|^)" + '(?:"([^"]*(?:""[^"]*)*)"|' + '([^"\\,\\r\\n]*))',
    "gi"
  );

  const fileContent = await readFileContents(recordFile);
  const regexMatch = objPattern.exec(fileContent);
  const column1Value = regexMatch
    ? regexMatch[2]
      ? regexMatch[2].replace(new RegExp('""', "g"), '"')
      : regexMatch[3]
    : "";
  return fileUploadRecordTypes.find((x) => x === column1Value) ?? "";
};

export default function FileUploadTable({ className }: { className?: string }) {
  const queryClient = useQueryClient();
  const leaContext = useRouteContext({ from: "/$leaid" });
  const esiidPath = leaContext.currentUserOrg.esiIdPath;

  const [fileEntries, setFileEntries] = useState<FileResponse[]>([]);
  const [invalidTypeFiles, setInvalidTypeFiles] = useState<File[]>([]);
  const [duplicateNameFiles, setDuplicateNameFiles] = useState<File[]>([]);

  const fileCount = fileEntries.length;
  const failCount = fileEntries.filter((f) => f.status === "Failed").length;

  const {
    control,
    handleSubmit,
    register,
    getValues,
    formState: { isDirty, isValid, isSubmitting },
  } = useForm<UploadData>({
    defaultValues: {
      files: [],
    },
  });

  const isSubmitBlocked = getValues("files").some(entry => !entry.recordType) || !(isDirty && isValid);

  const { fields, remove, replace } = useFieldArray({
    name: "files",
    control,
  });

  const handleFilesDrop = (newFiles: File[]) => {
    setInvalidTypeFiles([]);
    setDuplicateNameFiles([]);

    const { invalidTypeFiles, duplicateNameFiles, validFiles } = validateFiles(
      newFiles,
      getValues("files").map((f) => f.file),
      fileTypes,
      fileExtensions
    );
    if (invalidTypeFiles.length > 0) setInvalidTypeFiles(invalidTypeFiles);
    if (duplicateNameFiles.length > 0)
      setDuplicateNameFiles(duplicateNameFiles);
    if (validFiles.length > 0)
      addValidFiles(validFiles).catch((error) => {
        throw error;
      });
  };

  const addValidFiles = async (incomingFiles: File[]) => {
    if (incomingFiles.length === 0) return;

    const existingNames = new Set(fileEntries.map((f) => f.file.name));

    let newEntries: FileEntry[] = incomingFiles
      .filter((file) => !existingNames.has(file.name))
      .map((file) => ({
        file,
        recordType: "CSPPIDCandidate",
      }));

    if (newEntries.length === 0) return [];

    const existingEntries = getValues("files").map((field) => ({
      file: field.file,
      recordType: field.recordType,
    }));
    const combinedEntries: FileResponse[] = [
      ...existingEntries,
      ...newEntries,
    ].sort((a, b) => a.file.name.localeCompare(b.file.name));

    replace(combinedEntries);
    setFileEntries(combinedEntries);
  };

  const removeFile = (index: number) => {
    if (index < 0 || index >= fileEntries.length) return;
    remove(index);
    setFileEntries((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadMutation = useMutation({
    mutationFn: async ({ file, recordType }: FileEntry) => {
      const formData = new FormData();
      formData.append("csvFile", file);
      formData.append("recordType", recordType);

      return await fetchAuthenticated(
        leaContext,
        `/${leaContext.currentUserOrg.esiIdPath}/files/ajax/UploadMatchFileAjax`,
        {
          method: "POST",
          headers: { Accept: "application/json" },
          body: formData,
        }
      );
    },
  });

  const handleSingleFileSubmit = async (entry: FileEntry) => {
    setFileEntries((prev) =>
      prev.map((f) =>
        entry.file.name === f.file.name ? { ...entry, status: "Uploading" } : f
      )
    );

    try {
      await uploadMutation.mutateAsync({
        file: entry.file,
        recordType: entry.recordType,
      });
      await queryClient.invalidateQueries({
        queryKey: getCandidateSubmissionsQueryKey(esiidPath),
      });
      setFileEntries((prev) =>
        prev.filter((f) => entry.file.name !== f.file.name)
      );
    } catch (error) {
      setFileEntries((prev) =>
        prev.map((f) =>
          entry.file.name === f.file.name
            ? {
                ...entry,
                status: "Failed",
                error: (error as Error).message,
              }
            : f
        )
      );
    }
  };

  const onSubmit: SubmitHandler<UploadData> = async (data: UploadData) => {
    setInvalidTypeFiles([]);
    setDuplicateNameFiles([]);

    await Promise.all(data.files.map((entry) => handleSingleFileSubmit(entry)));

    // Sync form state after all uploads complete — use filename matching
    // instead of indices since the array may have shifted during concurrent uploads
    const remainingFiles = fileEntries.filter((f) => f.status === "Failed");
    replace(
      remainingFiles.map(({ file, recordType }) => ({ file, recordType }))
    );
    setFileEntries(remainingFiles);
  };

  return (
    <>
      <Card className={className}>
        {/* Header row */}
        <div className="d-flex align-items-start justify-content-between mb-3">
          <div>
            <div className="fs-5 fw-bold" style={{ color: "#0d2d4a" }}>
              Selected Files
            </div>
            <div
              style={{
                height: "1.5rem",
                fontSize: "0.875rem",
                color: "#6c757d",
              }}
            >
              {!isSubmitting && fields.length > 0 && (
                <span>
                  {fields.length} file{fields.length !== 1 ? "s" : ""} selected
                </span>
              )}
              {isSubmitting && (
                <span role="alert">
                  Uploading {fileCount} file{fileCount !== 1 ? "s" : ""}…
                </span>
              )}
            </div>
          </div>
          <BrowseFilesButton
            onSelect={handleFilesDrop}
            fileTypes={fileTypes}
            acceptExtensions={fileExtensions}
            multiple={true}
          />
        </div>

        {/* Alerts */}
        {invalidTypeFiles.length > 0 && (
          <Alert
            variant="danger"
            dismissible
            onClose={() => setInvalidTypeFiles([])}
          >
            <p>
              Unable to add the following files. Only .csv files are allowed:
            </p>
            <ul>
              {invalidTypeFiles.sort(compareFileNames).map((file) => (
                <li key={file.name}>{file.name}</li>
              ))}
            </ul>
          </Alert>
        )}

        {duplicateNameFiles.length > 0 && (
          <Alert
            variant="danger"
            dismissible
            onClose={() => setDuplicateNameFiles([])}
          >
            <p>
              Unable to add the following files. A file of the same name is
              already added to this batch:
            </p>
            <ul>
              {duplicateNameFiles.sort(compareFileNames).map((file) => (
                <li key={file.name}>{file.name}</li>
              ))}
            </ul>
          </Alert>
        )}

        {!isSubmitting && failCount > 0 && (
          <Alert variant="danger" dismissible className="mb-3">
            <p>
              The following {failCount} upload{failCount !== 1 ? "s" : ""}{" "}
              failed:
            </p>
            <ul>
              {fileEntries
                .filter((f) => f.status === "Failed")
                .map((entry) => (
                  <li key={`${entry.file.name} ${entry.file.lastModified}`}>
                    {entry.file.name} - {entry.error || "Unknown error"}
                  </li>
                ))}
            </ul>
          </Alert>
        )}

        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Table header */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr auto",
              borderBottom: "1px solid #dee2e6",
              paddingBottom: "0.5rem",
              marginBottom: "0",
              fontWeight: 600,
              fontSize: "0.875rem",
              color: "#212529",
            }}
          >
            <span>Filename</span>
            <span>Record Type</span>
            <span>File Size</span>
            <span style={{ minWidth: "4rem" }} />
          </div>

          {/* File rows or empty state */}
          {fields.length > 0 ? (
            fields.map((field, index) => {
              const entry = fileEntries.find(
                (f) => f.file.name === field.file.name
              );
              return (
                <div
                  key={field.id}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr auto",
                    alignItems: "center",
                    borderBottom: "1px solid #f0f2f4",
                    padding: "0.625rem 0",
                    fontSize: "0.9rem",
                  }}
                >
                  <span className="text-truncate pe-3">{field.file.name}</span>
                  <span className="text-muted pe-3">
                    {field.recordType || "—"}
                  </span>
                  <span className="text-muted">
                    {formatNumber(field.file.size / 1000, 1)} KB
                  </span>
                  <div
                    className="d-flex align-items-center gap-2"
                    style={{ minWidth: "4rem", justifyContent: "flex-end" }}
                  >
                    {entry?.status === "Uploading" && (
                      <TooltipTrigger tooltipText="Uploading">
                        <Spinner
                          variant="secondary"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                        />
                      </TooltipTrigger>
                    )}
                    {entry?.status === "Failed" && (
                      <TooltipTrigger tooltipText="Failed upload">
                        <Icon name="error" className="text-danger" />
                      </TooltipTrigger>
                    )}
                    {!isSubmitting && (
                      <TooltipTrigger tooltipText="Remove file">
                        <button
                          type="button"
                          className="btn btn-light btn-sm"
                          onClick={() => removeFile(index)}
                          aria-label="Remove file"
                        >
                          <Icon name="trash" />
                        </button>
                      </TooltipTrigger>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            /* ✅ UPDATED: Improved empty state — bigger icon, stronger text hierarchy */
            <div
              style={{
                border: "1.5px dashed #cbd5e1",
                borderRadius: "0.5rem",
                margin: "1rem 0",
                padding: "3rem 1rem",
                textAlign: "center",
              }}
            >
              <TbFileText
                size={48}
                color="#64748b"
                style={{ marginBottom: "1rem" }}
              />
              <p
                className="mb-2"
                style={{
                  color: "#0f172a",
                  fontSize: "1.1rem",
                  fontWeight: 600,
                }}
              >
                No files selected
              </p>
              <p
                className="mb-1"
                style={{ color: "#475569", fontSize: "1rem" }}
              >
                Drag &amp; drop files anywhere in the window or click{" "}
                <strong>Browse Files</strong> to get started.
              </p>
              <p
                className="mb-0"
                style={{ color: "#64748b", fontSize: "1rem" }}
              >
                Accepted file types: {fileTypesLabel}
              </p>
            </div>
          )}

          {/* Submit */}
          <div className="d-flex justify-content-end mt-3">
            <LoaderButton
              variant="primary"
              type="submit"
              disabled={isSubmitting}
              aria-disabled={isSubmitBlocked}
              style={isSubmitBlocked ? { opacity: 0.65 } : undefined}
              onClick={(e) => { if (isSubmitBlocked) e.preventDefault(); }}
              loading={isSubmitting}
            >
              Submit Files
            </LoaderButton>
          </div>
        </form>
      </Card>

      <FullPageDropZone onDropFiles={handleFilesDrop} />
    </>
  );
}